import { CloseIcon } from "@mantine/core";
import { Button, Drawer, Tooltip } from "antd";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, BrandGAds, BrandGAnalytics, BrandMeta, BrandShopify, CheckIcon, MinusIcon, OogwaiLogo2Full, PlusIcon, SalePlan } from "../../components/Icons/iconImages";
import { useEffect, useMemo, useState } from "react";
import { useCreatePaymentLinkMutation, useGetCurrentPlanQuery } from "../../services/stripe";
import Loading from "../../components/Loading";
import { toast } from "../../components/Toast/toast";
import ReactGA from 'react-ga4';

const Checkout = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [createPaymentLink, { isLoading: isCreatingPaymentLink }] =
        useCreatePaymentLinkMutation();

    const {
        data: getCurrentPlan,
        isLoading: currentPlanLoading,
    } = useGetCurrentPlanQuery("", {
        refetchOnFocus: true,
    });

    const plans = location?.state?.data || {};
    const queryCheckout = location?.state?.query || false;
    const integration = location?.state?.integration || false;

    console.log("queryCheckout", queryCheckout);

    const [analytics, setAnalytics] = useState(false);
    const [adPlatform, setAdPlatform] = useState(false);
    const [shopPlatform, setShopPlatform] = useState(false);
    const [selected, setSelected] = useState("Annual");

    const [queryCheckoutAmount, setQueryCheckoutAmount] = useState(10);
    const [queryQuantity, setQueryQuantity] = useState(1);

    const tabs = ["Monthly", "Quarterly", "Annual"];

    // Pro-rate calculation helper functions
    const calculateRemainingDays = (expiryDate: string): number => {
        if (!expiryDate) return 0;
        const today = new Date();
        const expiry = new Date(expiryDate);
        const diffTime = expiry.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays > 0 ? diffDays : 0;
    };

    const calculateProRateCredit = (currentPlan: any): number => {
        if (!currentPlan?.plan_expiry_date || !currentPlan?.amount_paid) return 0;

        const remainingDays = calculateRemainingDays(currentPlan.plan_expiry_date);
        if (remainingDays === 0) return 0;

        // Calculate daily rate based on current plan
        let totalDaysInPlan = 30; // Default to monthly
        if (currentPlan.plan === "Quarterly") totalDaysInPlan = 90;
        else if (currentPlan.plan === "Annual") totalDaysInPlan = 365;

        const dailyRate = currentPlan.amount_paid / totalDaysInPlan;
        const proRateCredit = dailyRate * remainingDays;

        return Math.round(proRateCredit * 100) / 100; // Round to 2 decimal places
    };

    const proRateCredit = useMemo(() => {
        return calculateProRateCredit(getCurrentPlan);
    }, [getCurrentPlan]);

    const [plan, setPlan] = useState({
        planPeriod: "Monthly",
        price: 20,
        // price: 5,
        duration: 1,
        currency: "$",
    });

    const isTwoOrMoreTrue = useMemo(() => {
        return [analytics, adPlatform, shopPlatform].filter(Boolean).length;
    }, [analytics, adPlatform, shopPlatform]);

    const showAddOn = useMemo(() => plan.price / 2, [plan.price]);

    const addOnAmount = useMemo(() => {
        if (isTwoOrMoreTrue >= 3) return (plan.price / 2) * 2;
        if (isTwoOrMoreTrue === 2) return ((plan.price / 2) * plan.duration);
        return 0;
    }, [isTwoOrMoreTrue, plan.price, plan.duration]);
    console.log("isTwoOrMoreTrue", isTwoOrMoreTrue, addOnAmount, plan);


    const purchaseObject = useMemo(() => {
        const baseAmount = isTwoOrMoreTrue >= 1 && integration ? (addOnAmount) : (plan.price * plan.duration + addOnAmount);
        const finalAmount = Math.max(0, baseAmount - proRateCredit); // Ensure amount is not negative
        const roundedFinalAmount = Math.round(finalAmount * 100) / 100; // Round to 2 decimal places

        return {
            analytics_integration: analytics ? "paid" : "free",
            ad_platform_integration: adPlatform ? "paid" : "free",
            shop_front_integration: shopPlatform ? "paid" : "free",
            purchase_plan: plan.planPeriod,
            amount: roundedFinalAmount,
            pro_rate_credit: proRateCredit, // Include pro-rate credit for backend processing
            original_amount: baseAmount,
        };
    }, [analytics, adPlatform, shopPlatform, plan, addOnAmount, proRateCredit, integration, isTwoOrMoreTrue]);

    useEffect(() => {
        if (getCurrentPlan) {
            setAnalytics(getCurrentPlan.google_analytics === "paid");
            setAdPlatform(getCurrentPlan.googleads_meta === "paid");
            setShopPlatform(getCurrentPlan.shopify === "paid");
            // setPlan((prev) => ({
            //     ...prev,
            //     planPeriod:
            //         getCurrentPlan.plan === "Monthly"
            //             ? "Monthly"
            //             : getCurrentPlan.plan === "Quarterly"
            //                 ? "Quarterly"
            //                 : "Annual",
            //     price: getCurrentPlan.amount_paid,
            // }));
        }
    }, [getCurrentPlan]);

    useEffect(() => {
        if (plans?.planPeriod) {
            setPlan(plans);
            setSelected(
                plans.planPeriod === "Monthly"
                    ? "Monthly"
                    : plans.planPeriod === "Quarterly"
                        ? "Quarterly"
                        : "Annual"
            );
        }
    }, [plans]);

    const navigatePaymentLink = () => {
        const paymentMetadata = {
            product_name: queryCheckout ? "Query Addon" : "Pro Plan",
            amount: queryCheckout ? queryQuantity * queryCheckoutAmount : purchaseObject.amount,
            plan_period: queryCheckout ? "One-time" : plan.planPeriod,
            currency: plan.currency,
            timestamp: new Date().toISOString(),
            quantity: queryCheckout ? queryQuantity : plan.duration,
            integrations: !queryCheckout ? {
                analytics: analytics,
                adPlatform: adPlatform,
                shopPlatform: shopPlatform,
                count: isTwoOrMoreTrue
            } : null,
        };

        if (queryCheckout) {
            localStorage.setItem("queryLimit", JSON.stringify(queryQuantity * 100));
            localStorage.setItem("paymentMetadata", JSON.stringify(paymentMetadata));
        } else {
            localStorage.setItem("purchaseObject", JSON.stringify(purchaseObject));
            localStorage.setItem("paymentMetadata", JSON.stringify(paymentMetadata));
        }

        // Send GA4 begin_checkout event before creating payment link
        if (queryCheckout) {

            ReactGA.event('begin_checkout', {
                value: paymentMetadata.amount,
                currency: 'USD',
                checkout_timestamp: paymentMetadata.timestamp,
                items: [
                    {
                        item_id: 'query_addon',
                        item_name: paymentMetadata.product_name,
                        item_category: 'Query Pack',
                        item_variant: paymentMetadata.plan_period,
                        price: paymentMetadata.amount,
                        quantity: paymentMetadata.quantity,
                    }
                ]
            });
        } else {

            ReactGA.event('begin_checkout', {
                value: paymentMetadata.amount,
                currency: 'USD',
                checkout_timestamp: paymentMetadata.timestamp,
                items: [
                    {
                        item_id: 'pro_plan',
                        item_name: paymentMetadata.product_name,
                        item_category: 'Subscription Plan',
                        item_variant: paymentMetadata.plan_period,
                        price: paymentMetadata.amount,
                        quantity: 1,
                        duration_months: paymentMetadata.quantity,
                        analytics_enabled: paymentMetadata.integrations?.analytics || false,
                        ad_platform_enabled: paymentMetadata.integrations?.adPlatform || false,
                        shop_platform_enabled: paymentMetadata.integrations?.shopPlatform || false,
                        total_integrations: paymentMetadata.integrations?.count || 0,
                    }
                ]
            });
        }

        let payload = {
            product_name: paymentMetadata.product_name,
            price: paymentMetadata.amount,
        };

        createPaymentLink(payload)
            .unwrap()
            .then((response) => {
                if (response?.payment_link_url) {
                    // Send GA4 event for successful payment link creation

                    ReactGA.event('payment_link_created', {
                        payment_link_url: response.payment_link_url,
                        product_name: paymentMetadata.product_name,
                        plan_name: paymentMetadata.plan_period,
                        amount: paymentMetadata.amount,
                        currency: 'USD',
                        timestamp: paymentMetadata.timestamp,
                        purchase_type: queryCheckout ? 'query_addon' : 'plan_upgrade',
                    });

                    window.location.href = response.payment_link_url;
                } else {
                    console.error("Invalid response from createPaymentLink:", response);

                    // Send GA4 event for payment link creation failure

                    ReactGA.event('payment_link_failed', {
                        error_type: 'invalid_response',
                        product_name: paymentMetadata.product_name,
                        amount: paymentMetadata.amount,
                    });
                }
            })
            .catch((error) => {
                console.error("Error creating payment link:", error);

                // Send GA4 event for payment link creation error

                ReactGA.event('payment_link_failed', {
                    error_type: 'api_error',
                    error_message: error?.message || 'Unknown error',
                    product_name: paymentMetadata.product_name,
                    amount: paymentMetadata.amount,
                });

                toast.error("Failed to create payment link. Please try again.");
            });
    };

    if (isCreatingPaymentLink || currentPlanLoading) return <Loading />;

    return (
        <div>
            Checkout Page
            <Drawer
                closable={false}
                open={true}
                style={{
                    top: 0,
                    padding: 0,
                    maxWidth: '100vw',
                }}
                bodyStyle={{
                    padding: 0,
                    borderRadius: 0,
                }}
                width="100vw"
                className="rounded-none"
            >

                <>
                    <div className="p-3 sm:p-4 border-b border-slate-300 flex justify-between items-center">
                        <div className="header-logo">
                            <Link to="/"><OogwaiLogo2Full className="h-[1.5rem] sm:h-[2rem]" /></Link>
                        </div>

                        <div className="text-[#414651] cursor-pointer flex gap-1 sm:gap-2 px-3 py-1.5 sm:p-2 rounded-full item-center text-xs sm:text-sm font-semibold border border-slate-300" onClick={() => navigate("/company-settings/plan")}>
                            <span className="hidden sm:inline">Cancel</span>
                            <CloseIcon className="closeIcon" />
                        </div>
                    </div>

                    <div className="flex flex-col lg:grid lg:grid-cols-12 h-full">
                        <div className="lg:col-span-7 xl:col-span-8 bg-[#FAFAFA]">
                            <div className="px-4 sm:px-6 md:px-8">
                                <div className="flex items-center gap-2 mt-3 sm:mt-4 text-sm sm:text-base text-[#535862] font-semibold cursor-pointer" onClick={() => navigate("/company-settings/plan")}>
                                    <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                                    All Plans
                                </div>
                                <p className="text-[#181D27] mt-2 font-semibold text-base sm:text-lg">Purchase {queryCheckout ? "Queries" : "Plan"}</p>
                                <hr className="border-gray-300 border-b mt-3 sm:mt-4" />
                                {
                                    queryCheckout ? (
                                        <div className="mt-4 sm:mt-8 bg-white p-3 sm:p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 rounded-xl border border-[#E9EAEB]">
                                            <p className="text-[#181D27] font-semibold text-sm sm:text-base">Oogwai Queries Pack</p>
                                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 w-full sm:w-auto">
                                                <p className="text-[#717680] font-semibold text-sm sm:text-base">Packs of 100</p>
                                                <div className="flex items-center gap-2 sm:gap-3 bg-[#F5F5F5] p-2 rounded-full">
                                                    <button className="bg-white h-8 w-8 sm:h-9 sm:w-9 rounded-full flex items-center justify-center" onClick={() => setQueryQuantity(queryQuantity - 1)} disabled={queryQuantity <= 1}><MinusIcon className="color-[E9EAEB] w-3 h-3 sm:w-4 sm:h-4" /></button>
                                                    <p className="text-[#181D27] font-semibold text-sm sm:text-base min-w-[2rem] text-center">{queryQuantity}</p>
                                                    <button className="bg-white h-8 w-8 sm:h-9 sm:w-9 rounded-full flex items-center justify-center" onClick={() => setQueryQuantity(queryQuantity + 1)}><PlusIcon className="w-3 h-3 sm:w-4 sm:h-4" /></button>
                                                </div>
                                            </div>
                                        </div>
                                    )
                                        :
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mt-3 sm:mt-4 mb-4 sm:mb-0">
                                            <div className={`${analytics ? " border border-[#609F81] border-2 bg-[#F5FAF7] " : " border border-slate-300 bg-white "} rounded-xl p-3 sm:p-4 relative min-h-52 sm:min-h-64 `}>
                                                <p className="text-[#181D27] font-semibold text-sm sm:text-base">Analytics Integration</p>
                                                <div className="flex items-center gap-2 mt-3 sm:mt-4">
                                                    <BrandGAnalytics className="h-[20px] w-[20px] sm:h-[24px] sm:w-[24px]" />
                                                    <p className="text-[#535862] font-normal text-xs sm:text-base">Google Analytics integration</p>
                                                </div>
                                                <Button
                                                    type={analytics ? "primary" : "default"}
                                                    icon={
                                                        analytics ? <CheckIcon
                                                            className={"h-3 w-3 sm:h-3.5 sm:w-3.5 text-white shadow-sm"}
                                                        />
                                                            :
                                                            <PlusIcon className={"h-3 w-3 sm:h-3.5 sm:w-3.5"} />
                                                    }
                                                    className="absolute bottom-3 sm:bottom-4 text-xs sm:text-sm"
                                                    onClick={() => setAnalytics(!analytics)}
                                                >
                                                    {analytics ? "Added" : `Add ${isTwoOrMoreTrue >= 1 ? `$${showAddOn}` : ""}`}
                                                </Button>
                                            </div>

                                            <div className={`${adPlatform ? " border border-[#609F81] border-2 bg-[#F5FAF7] " : " border border-slate-300 bg-white "} rounded-xl p-3 sm:p-4 relative min-h-52 sm:min-h-64 `}>
                                                <p className="text-[#181D27] font-semibold text-sm sm:text-base">Ad-Platform Integration</p>
                                                <div className="flex items-center gap-2 mt-3 sm:mt-4">
                                                    <BrandGAds className="h-[20px] w-[20px] sm:h-[24px] sm:w-[24px]" />
                                                    <p className="text-[#535862] font-normal text-xs sm:text-base">Google Ads integration</p>
                                                </div>

                                                <div className="flex items-center gap-2 mt-3 sm:mt-4">
                                                    <BrandMeta className="h-[20px] w-[20px] sm:h-[24px] sm:w-[24px]" />
                                                    <p className="text-[#535862] font-normal text-xs sm:text-base">Meta Ads integration</p>
                                                </div>

                                                <Button
                                                    type={adPlatform ? "primary" : "default"}
                                                    icon={
                                                        adPlatform ? <CheckIcon
                                                            className={"h-3 w-3 sm:h-3.5 sm:w-3.5 text-white shadow-sm"}
                                                        />
                                                            :
                                                            <PlusIcon className={"h-3 w-3 sm:h-3.5 sm:w-3.5"} />
                                                    }
                                                    className="absolute bottom-3 sm:bottom-4 text-xs sm:text-sm"
                                                    onClick={() => setAdPlatform(!adPlatform)}
                                                >
                                                    {adPlatform ? "Added" : `Add ${isTwoOrMoreTrue >= 1 ? `$${showAddOn}` : ""}`}
                                                </Button>
                                            </div>

                                            {/* <div className={`${shopPlatform ? " border border-[#609F81] border-2 bg-[#F5FAF7] " : " border border-slate-300 bg-white "} rounded-xl p-4 relative min-h-64 `}>
                                                <p className="text-[#181D27] font-semibold text-base">Shop-front Integration</p>
                                                <div className="flex items-center gap-2 mt-4">
                                                    <BrandShopify className="h-[24px] w-[24px]" />
                                                    <p className="text-[#535862] font-normal text-base">Shopify integration</p>
                                                </div>
                                                <Button
                                                    type={shopPlatform ? "primary" : "default"}
                                                    icon={
                                                        shopPlatform ? <CheckIcon
                                                            className={"h-3.5 w-3.5 text-white shadow-sm"}
                                                        />
                                                            :
                                                            <PlusIcon className={"h-3.5 w-3.5 "} />
                                                    }
                                                    className="absolute bottom-4"
                                                    onClick={() => setShopPlatform(!shopPlatform)}
                                                >
                                                    {shopPlatform ? "Added" : `Add ${isTwoOrMoreTrue >= 1 ? `$${showAddOn}` : ""}`}
                                                </Button>
                                            </div> */}

                                        </div>

                                }
                            </div>
                        </div>
                        <div className="lg:col-span-5 xl:col-span-4 border-t lg:border-t-0 lg:border-l border-slate-300 bg-white p-4 sm:p-6 relative">
                            <p className="text-[#181D27] mt-4 sm:mt-8 font-semibold text-base sm:text-lg">Your Order</p>
                            <hr className="border-gray-300 border-b mt-3 sm:mt-4" />
                            {
                                queryCheckout ?
                                    <div>
                                        <div className="mt-3 sm:mt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                                            <div>
                                                <p className="text-[#181D27] font-semibold text-sm sm:text-base">Oogwai Queries Pack</p>
                                                <p className="text-[#717680] font-normal text-xs sm:text-sm">{queryQuantity} X of 100 queries</p>
                                            </div>
                                            <p className="text-[#717680] font-semibold text-sm sm:text-base">{`${plan.currency}${queryQuantity * queryCheckoutAmount}`}</p>
                                        </div>
                                        <hr className="border-gray-300 border-b mt-4 sm:mt-6" />
                                        <p className="text-[#717680] font-normal text-xs sm:text-sm mt-2 sm:mt-3">The query credits will roll-over to next month.</p>
                                    </div>
                                    :
                                    <div className="flex  justify-center items-center gap-1 sm:gap-0 mt-4 sm:mt-6 bg-gray-100 rounded-full border border-slate-300 p-1">
                                        {tabs.map((tab) => {
                                            return (
                                                <button
                                                    key={tab}
                                                    onClick={() => {
                                                        setSelected(tab);
                                                        if (tab === 'Monthly') {
                                                            setPlan({
                                                                ...plan,
                                                                planPeriod: 'Monthly',
                                                                price: 30,
                                                                duration: 1
                                                            });
                                                        } else if (tab === 'Quarterly') {
                                                            setPlan({
                                                                ...plan,
                                                                planPeriod: 'Quarterly',
                                                                price: 25,
                                                                duration: 3
                                                            });
                                                        } else {
                                                            setPlan({
                                                                ...plan,
                                                                planPeriod: 'Annual',
                                                                price: 20,
                                                                duration: 12
                                                            });
                                                        }
                                                    }}
                                                    className={`px-2 sm:px-3   py-1.5 sm:py-2 rounded-full text-[10px] sm:text-xs lg:text-sm font-medium transition-colors duration-200 whitespace-nowrap
            ${selected === tab
                                                            ? 'bg-white shadow text-[#414651] border border-slate-300'
                                                            : 'text-[#717680] hover:text-gray-700'}`}
                                                >
                                                    {tab?.includes("Annual") ? `${tab} (save 33%)` : tab?.includes("Quarterly") ? `${tab} (save 15%)` : tab}
                                                </button>
                                            );
                                        })}
                                    </div>

                            }


                            {!queryCheckout && isTwoOrMoreTrue >= 1 && <>  <hr className="border-gray-300 border-b mt-4 sm:mt-6" />

                                <div className="flex flex-col sm:flex-row justify-between gap-2 sm:gap-4 mt-3 sm:mt-4">
                                    <div className="flex-1">
                                        <p className="text-[#181D27] font-semibold text-sm sm:text-base">Paid Plan</p>
                                        <p className="text-[#717680] font-normal text-xs sm:text-sm">1 Integration and 300 queries/mo ({`${plan.currency}${plan.price}`} × {`${plan.duration}`} months, billed {`${plan.planPeriod}`})</p>
                                    </div>
                                    <div className="flex-shrink-0">
                                        <div className="flex gap-1 items-end">
                                            <p className="text-[#717680] font-semibold text-sm sm:text-base line-through">$360</p>
                                            <p className="text-[#181D27] font-semibold text-sm sm:text-base">{`${plan.currency}${plan.price * plan.duration}`}</p>
                                        </div>
                                    </div>

                                </div>
                                <hr className="border-gray-300 border-b mt-4 sm:mt-6" />
                            </>
                            }
                            {isTwoOrMoreTrue >= 2 && <>
                                <div className="flex flex-col sm:flex-row justify-between gap-2 sm:gap-4 mt-3 sm:mt-4">
                                    <div className="flex-1">
                                        <p className="text-[#181D27] font-semibold text-sm sm:text-base">Add-on Integrations</p>
                                        <p className="text-[#717680] font-normal text-xs sm:text-sm">{`${isTwoOrMoreTrue >= 3 ? "+300" : "+200"}`} queries/mo ({`$${showAddOn}`} × {`$${plan.duration}`} months, billed annually)</p>
                                    </div>
                                    <div className="flex-shrink-0 text-right">
                                        <div className="flex gap-1 items-end">
                                            <p className="text-[#717680] font-semibold text-sm sm:text-base line-through">$720</p>
                                            <p className="text-[#181D27] font-semibold text-sm sm:text-base">${addOnAmount}</p>
                                        </div>
                                    </div>
                                </div>
                                <hr className="border-gray-300 border-b mt-4 sm:mt-6" />
                            </>}

                            {

                                queryCheckout ?
                                    <div className="mt-6 sm:mt-8 lg:[@media(min-height:650px)]:absolute lg:bottom-24 lg:left-4 lg:right-4">
                                        <hr className="border-gray-300 border-b mt-4 sm:mt-6" />
                                        <div className="flex justify-between items-center gap-2 mt-3 sm:mt-4">
                                            <div>
                                                <p className="text-[#181D27] font-semibold text-sm sm:text-base">Total</p>
                                            </div>
                                            <div className="text-right">
                                                <div className="flex gap-1 items-end">
                                                    <p className="text-[#717680] font-semibold text-sm sm:text-base line-through"></p>
                                                    <p className="text-[#181D27] font-semibold text-sm sm:text-base">{`${plan.currency}${queryQuantity * queryCheckoutAmount}`}</p>
                                                </div>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => navigatePaymentLink()}
                                            disabled={queryQuantity * queryCheckoutAmount === 0}
                                            className={`${queryQuantity * queryCheckoutAmount > 0 ? 'text-white bg-[#609F81]' : 'text-[#A4A7AE] bg-[#F5F5F5]'} text-sm sm:text-base font-semibold rounded-full w-full mt-4 sm:mt-6 p-2 border border-slate-300`}
                                        >
                                            Checkout
                                        </button>

                                    </div>
                                    :
                                    <div className="mt-6 sm:mt-8 lg:[@media(min-height:750px)]:absolute lg:bottom-24 lg:left-6 lg:right-6 pb-4 sm:pb-0">
                                        {/* Show subtotal if pro-rate credit exists */}
                                        {proRateCredit > 0 && (
                                            <>
                                                {/* <hr className="border-gray-300 border-b mt-4 sm:mt-6" />
                                                <div className="flex justify-between items-center gap-2 mt-3 sm:mt-4">
                                                    <div>
                                                        <p className="text-[#717680] font-normal text-sm sm:text-base">Subtotal</p>
                                                    </div>
                                                    <div className="text-right">
                                                        {analytics || shopPlatform || adPlatform ? (
                                                            <p className="text-[#717680] font-normal text-sm sm:text-base">{`${plan.currency}${purchaseObject.original_amount}`}</p>
                                                        ) : (
                                                            <p className="text-[#717680] font-normal text-sm sm:text-base">{`${plan.currency}0`}</p>
                                                        )}
                                                    </div>
                                                </div> */}

                                                {/* <hr className="border-gray-300 border-b mt-3 sm:mt-4" /> */}
                                                {/* Pro-rate credit line */}
                                                <div className="flex justify-between items-start gap-4 sm:gap-6 mt-2 sm:mt-3 mb-2">
                                                    <div className="flex-1 min-w-0 pr-2">
                                                        <p className="text-[#079455] font-semibold text-sm sm:text-base mb-1 leading-snug">Pro-rate Credit</p>
                                                        <p className="text-[#717680] font-normal text-xs sm:text-sm break-words leading-relaxed">
                                                            {calculateRemainingDays(getCurrentPlan?.plan_expiry_date)} days remaining on current plan
                                                        </p>
                                                    </div>
                                                    <div className="text-right flex-shrink-0 self-start pt-0.5">
                                                        <p className="text-[#079455] font-semibold text-sm sm:text-base whitespace-nowrap">-{`${plan.currency}${proRateCredit.toFixed(2)}`}</p>
                                                    </div>
                                                </div>
                                            </>
                                        )}

                                        <hr className="border-gray-300 border-b mt-3 sm:mt-4" />
                                        <div className="flex justify-between items-center gap-2 mt-2 sm:mt-3">
                                            <div>
                                                <p className="text-[#181D27] font-semibold text-sm sm:text-base">Annual Total</p>
                                            </div>
                                            <div className="text-right">
                                                <div className="flex gap-1 items-end">
                                                    <p className="text-[#717680] font-semibold text-sm sm:text-base line-through"></p>
                                                    {analytics || shopPlatform || adPlatform ? <p className="text-[#181D27] font-semibold text-sm sm:text-base">{`${plan.currency}${Number(purchaseObject?.amount).toFixed(2)}`}</p> : <p className="text-[#181D27] font-semibold text-sm sm:text-base">{`${plan.currency}0`}</p>}
                                                </div>
                                            </div>

                                        </div>
                                        <div className="bg-[#F5FAF7] flex items-center p-2.5 sm:p-3 gap-2 mt-2 sm:mt-3">
                                            <SalePlan className="h-[20px] w-[20px] sm:h-[24px] sm:w-[24px] flex-shrink-0" />
                                            <p className="text-[#079455] font-semibold text-xs sm:text-sm">Total Annual Savings: $ 120</p>
                                        </div>
                                        <button
                                            onClick={() => navigatePaymentLink()}
                                            disabled={(!analytics && !shopPlatform && !adPlatform) || purchaseObject.amount === 0}
                                            className={`${(analytics || shopPlatform || adPlatform) && purchaseObject.amount > 0 ? ' text-white bg-[#609F81] ' : " text-[#A4A7AE]  bg-[#F5F5F5] "} text-sm sm:text-base font-semibold rounded-full w-full mt-3 sm:mt-4 p-2 border border-slate-300`}
                                        >
                                            Checkout
                                        </button>
                                    </div>}


                        </div>

                    </div>
                </>

            </Drawer>
        </div>
    )
}

export default Checkout;